/**
 * HR (Recursos Humanos) — single page with three tabs:
 *   - Vacaciones: pending/decided request list + create + approve/reject
 *   - Ausencias:  per-day absence calendar/list + create
 *   - Nómina:     payroll preview + generate boleta PDF
 *
 * Gated behind plan `hr_module` (Pro). The route is registered in App.js
 * inside the same plan-aware guard component that Layout's sidebar uses,
 * so non-Pro users get redirected to /upgrade?reason=hr_module before
 * rendering this page.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { formatMoney } from "@/lib/money";
import { PageLoader } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { useSubscription } from "@/lib/subscription";
import {
  Calendar, CalendarPlus, Check, X, Plane, FileDown, Calculator,
  UserCog, Trash2, AlertCircle, Crown,
} from "lucide-react";

const TIPO_VAC = ["Vacaciones", "Permiso", "Otro"];
const TIPO_AUS = [
  { value: "enfermedad", label: "Enfermedad" },
  { value: "permiso_con_goce", label: "Permiso con goce" },
  { value: "permiso_sin_goce", label: "Permiso sin goce" },
  { value: "injustificada", label: "Injustificada" },
];

const ESTADO_VAC_META = {
  pendiente: { label: "Pendiente", color: "bg-amber-50 text-amber-700 border-amber-200" },
  aprobada: { label: "Aprobada", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rechazada: { label: "Rechazada", color: "bg-red-50 text-red-700 border-red-200" },
};

export default function HR() {
  const { hasFeature } = useSubscription();
  const unlocked = hasFeature("hr_module");
  const [tab, setTab] = useState("vacaciones");
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [requests, setRequests] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [runs, setRuns] = useState([]);
  const [balances, setBalances] = useState({}); // {employee_id: {accrued, taken, pending, available}}

  // Dialogs
  const [newVacOpen, setNewVacOpen] = useState(false);
  const [newAbsOpen, setNewAbsOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!unlocked) { setLoading(false); return; }
    setLoading(true);
    try {
      const [empRes, vacRes, absRes, runsRes] = await Promise.all([
        api.get("/employees"),
        api.get("/hr/vacation-requests"),
        api.get("/hr/absences"),
        api.get("/hr/payroll/runs"),
      ]);
      setEmployees(empRes.data || []);
      setRequests(vacRes.data || []);
      setAbsences(absRes.data || []);
      setRuns(runsRes.data || []);
    } catch {
      toast.error("Error cargando datos de RRHH");
    } finally {
      setLoading(false);
    }
  }, [unlocked]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch all balances in parallel whenever employee list changes.
  useEffect(() => {
    if (!unlocked || !employees.length) return;
    Promise.all(employees.map(e => api.get(`/hr/vacation-balance/${e.id}`).then(r => [e.id, r.data]).catch(() => [e.id, null])))
      .then(arr => setBalances(Object.fromEntries(arr.filter(([, v]) => v))));
  }, [employees, unlocked]);

  if (!unlocked) {
    return (
      <div className="max-w-2xl mx-auto py-8" data-testid="hr-locked">
        <Card className="p-8 rounded-sm border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 text-center">
          <Crown className="h-12 w-12 mx-auto text-amber-600 mb-3" />
          <h2
            className="text-3xl font-black text-slate-900 mb-2"
            style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
          >
            Recursos Humanos
          </h2>
          <p className="text-sm text-slate-700 mb-1">
            Gestioná vacaciones, ausencias y nómina de tu equipo en un solo lugar.
          </p>
          <p className="text-xs text-slate-600 italic mb-4">
            Solicitudes con flujo de aprobación · calendario de ausencias · cálculo automático CCSS · boletas de pago en PDF.
          </p>
          <Link
            to="/upgrade?reason=hr_module"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold rounded-sm transition-colors"
            data-testid="hr-upgrade-btn"
          >
            <Crown className="h-4 w-4" />
            Activar con plan Pro
          </Link>
        </Card>
      </div>
    );
  }

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-6" data-testid="hr-page">
      <div>
        <h1
          className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight"
          style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
        >
          Recursos Humanos
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Gestioná vacaciones, ausencias y nómina de tu equipo.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 rounded-sm">
          <TabsTrigger value="vacaciones" className="rounded-sm" data-testid="hr-tab-vacations">
            <Plane className="h-4 w-4 mr-1.5" />
            Vacaciones
          </TabsTrigger>
          <TabsTrigger value="ausencias" className="rounded-sm" data-testid="hr-tab-absences">
            <Calendar className="h-4 w-4 mr-1.5" />
            Ausencias
          </TabsTrigger>
          <TabsTrigger value="nomina" className="rounded-sm" data-testid="hr-tab-payroll">
            <Calculator className="h-4 w-4 mr-1.5" />
            Nómina
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vacaciones" className="mt-4 space-y-4">
          <VacationsPanel
            employees={employees}
            requests={requests}
            balances={balances}
            onCreate={() => setNewVacOpen(true)}
            onApprove={async (r) => {
              await api.post(`/hr/vacation-requests/${r.id}/approve`);
              toast.success("Solicitud aprobada");
              fetchAll();
            }}
            onReject={(r) => setRejectTarget(r)}
            onDelete={async (r) => {
              if (!window.confirm(`¿Eliminar solicitud de ${r.employee_name}?`)) return;
              await api.delete(`/hr/vacation-requests/${r.id}`);
              toast.success("Solicitud eliminada");
              fetchAll();
            }}
          />
        </TabsContent>

        <TabsContent value="ausencias" className="mt-4 space-y-4">
          <AbsencesPanel
            employees={employees}
            absences={absences}
            onCreate={() => setNewAbsOpen(true)}
            onDelete={async (a) => {
              if (!window.confirm("¿Eliminar ausencia?")) return;
              await api.delete(`/hr/absences/${a.id}`);
              toast.success("Ausencia eliminada");
              fetchAll();
            }}
          />
        </TabsContent>

        <TabsContent value="nomina" className="mt-4 space-y-4">
          <PayrollPanel
            employees={employees}
            runs={runs}
            onChanged={fetchAll}
          />
        </TabsContent>
      </Tabs>

      <NewVacationDialog
        open={newVacOpen}
        onClose={() => setNewVacOpen(false)}
        employees={employees}
        onSaved={() => { setNewVacOpen(false); fetchAll(); }}
      />
      <NewAbsenceDialog
        open={newAbsOpen}
        onClose={() => setNewAbsOpen(false)}
        employees={employees}
        onSaved={() => { setNewAbsOpen(false); fetchAll(); }}
      />
      <RejectVacationDialog
        target={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onSaved={() => { setRejectTarget(null); fetchAll(); }}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── Vacations ── */

function VacationsPanel({ employees, requests, balances, onCreate, onApprove, onReject, onDelete }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button onClick={onCreate} className="btn-primary rounded-sm" data-testid="hr-vacations-new">
          <CalendarPlus className="h-4 w-4 mr-1.5" />
          Nueva solicitud
        </Button>
      </div>

      {/* Balances summary */}
      {employees.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="hr-vacations-balances">
          {employees.map((e) => {
            const b = balances[e.id] || { accrued: 0, taken: 0, pending: 0, available: 0 };
            const negative = b.available < 0;
            return (
              <Card key={e.id} className="p-3 rounded-sm border border-slate-200 bg-white">
                <p className="text-sm font-bold text-slate-900 truncate">{e.nombre_completo}</p>
                <div className="mt-1.5 grid grid-cols-4 gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  <span>Acum.</span><span>Tom.</span><span>Pend.</span><span className={negative ? "text-red-600" : "text-emerald-700"}>Disp.</span>
                </div>
                <div className="grid grid-cols-4 gap-1 mono text-sm">
                  <span>{b.accrued}</span>
                  <span>{b.taken}</span>
                  <span>{b.pending}</span>
                  <span className={`font-black ${negative ? "text-red-600" : "text-emerald-700"}`} data-testid={`hr-balance-${e.id}`}>{b.available}</span>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Requests list */}
      {requests.length === 0 ? (
        <EmptyState icon={Plane} title="Sin solicitudes" description="Cuando un empleado pida vacaciones, aparecerá acá." />
      ) : (
        <Card className="rounded-sm border border-slate-200">
          <div className="divide-y divide-slate-100">
            {requests.map((r) => {
              const meta = ESTADO_VAC_META[r.estado] || ESTADO_VAC_META.pendiente;
              return (
                <div key={r.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`hr-vac-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{r.employee_name}</p>
                    <p className="text-xs text-slate-600">
                      {r.fecha_inicio} → {r.fecha_fin} · <span className="font-bold">{r.dias} día{r.dias === 1 ? "" : "s"}</span> · {r.tipo}
                    </p>
                    {r.motivo && <p className="text-[11px] text-slate-500 italic mt-0.5 truncate">"{r.motivo}"</p>}
                    {r.decision_notas && <p className="text-[11px] text-slate-500 mt-0.5">Decisión: {r.decision_notas}</p>}
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${meta.color}`}>
                    {meta.label}
                  </span>
                  {r.estado === "pendiente" && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => onApprove(r)}
                        className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`hr-vac-approve-${r.id}`}
                      >
                        <Check className="h-3.5 w-3.5 mr-1" /> Aprobar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onReject(r)}
                        className="rounded-sm border-red-300 text-red-700 hover:bg-red-50"
                        data-testid={`hr-vac-reject-${r.id}`}
                      >
                        <X className="h-3.5 w-3.5 mr-1" /> Rechazar
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(r)}
                    className="rounded-sm text-slate-400 hover:text-red-600"
                    data-testid={`hr-vac-delete-${r.id}`}
                    title="Eliminar"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

function NewVacationDialog({ open, onClose, employees, onSaved }) {
  const [form, setForm] = useState({ employee_id: "", fecha_inicio: "", fecha_fin: "", tipo: "Vacaciones", motivo: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ employee_id: employees[0]?.id || "", fecha_inicio: "", fecha_fin: "", tipo: "Vacaciones", motivo: "" });
  }, [open, employees]);

  const submit = async () => {
    if (!form.employee_id || !form.fecha_inicio || !form.fecha_fin) {
      toast.error("Empleado, fecha inicio y fecha fin son obligatorios");
      return;
    }
    setSaving(true);
    try {
      await api.post("/hr/vacation-requests", form);
      toast.success("Solicitud creada");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear solicitud");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="hr-new-vac-dialog">
        <DialogHeader>
          <DialogTitle>Nueva solicitud de vacaciones</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Empleado</label>
            <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
              <SelectTrigger className="rounded-sm" data-testid="hr-new-vac-employee">
                <SelectValue placeholder="Seleccionar…" />
              </SelectTrigger>
              <SelectContent>
                {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.nombre_completo}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Desde</label>
              <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} className="rounded-sm" data-testid="hr-new-vac-start" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Hasta</label>
              <Input type="date" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} className="rounded-sm" data-testid="hr-new-vac-end" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Tipo</label>
            <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
              <SelectTrigger className="rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPO_VAC.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Motivo</label>
            <Textarea value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} rows={2} className="rounded-sm" data-testid="hr-new-vac-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="btn-primary rounded-sm" data-testid="hr-new-vac-submit">
            {saving ? "Guardando…" : "Crear solicitud"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectVacationDialog({ target, onClose, onSaved }) {
  const [motivo, setMotivo] = useState("");
  useEffect(() => { if (target) setMotivo(""); }, [target]);
  if (!target) return null;
  const submit = async () => {
    try {
      await api.post(`/hr/vacation-requests/${target.id}/reject`, null, { params: { motivo } });
      toast.success("Solicitud rechazada");
      onSaved();
    } catch {
      toast.error("Error al rechazar");
    }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="hr-reject-vac-dialog">
        <DialogHeader>
          <DialogTitle>Rechazar solicitud</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600 mb-2">
          ¿Rechazar la solicitud de <strong>{target.employee_name}</strong> ({target.fecha_inicio} → {target.fecha_fin})?
        </p>
        <Textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Motivo del rechazo (visible para el empleado)"
          rows={3}
          className="rounded-sm"
          data-testid="hr-reject-vac-reason"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancelar</Button>
          <Button onClick={submit} className="bg-red-600 hover:bg-red-700 rounded-sm text-white" data-testid="hr-reject-vac-submit">
            Rechazar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────────────────────────────────────── Absences ── */

function AbsencesPanel({ employees, absences, onCreate, onDelete }) {
  const [filterEmp, setFilterEmp] = useState("__all__");
  const filtered = useMemo(() =>
    filterEmp === "__all__" ? absences : absences.filter(a => a.employee_id === filterEmp),
    [absences, filterEmp],
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Select value={filterEmp} onValueChange={setFilterEmp}>
          <SelectTrigger className="rounded-sm w-64" data-testid="hr-abs-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los empleados</SelectItem>
            {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.nombre_completo}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={onCreate} className="btn-primary rounded-sm" data-testid="hr-abs-new">
          <CalendarPlus className="h-4 w-4 mr-1.5" />
          Registrar ausencia
        </Button>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Calendar} title="Sin ausencias" description="Registrá ausencias por enfermedad, permiso o injustificadas." />
      ) : (
        <Card className="rounded-sm border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Fecha</th>
                <th className="text-left px-3 py-2">Empleado</th>
                <th className="text-left px-3 py-2">Tipo</th>
                <th className="text-right px-3 py-2">Horas</th>
                <th className="text-left px-3 py-2">Justificada</th>
                <th className="text-left px-3 py-2">Motivo</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(a => {
                const tipoLabel = (TIPO_AUS.find(t => t.value === a.tipo) || {}).label || a.tipo;
                return (
                  <tr key={a.id} className="hover:bg-slate-50" data-testid={`hr-abs-row-${a.id}`}>
                    <td className="px-3 py-2 mono">{a.fecha}</td>
                    <td className="px-3 py-2 truncate max-w-[180px]">{a.employee_name}</td>
                    <td className="px-3 py-2">{tipoLabel}</td>
                    <td className="px-3 py-2 text-right mono">{a.horas}</td>
                    <td className="px-3 py-2">
                      {a.justificada ? <Check className="h-4 w-4 text-emerald-600" /> : <X className="h-4 w-4 text-red-600" />}
                    </td>
                    <td className="px-3 py-2 text-slate-600 truncate max-w-[200px]">{a.motivo}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => onDelete(a)} className="rounded-sm text-slate-400 hover:text-red-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function NewAbsenceDialog({ open, onClose, employees, onSaved }) {
  const [form, setForm] = useState({
    employee_id: "", fecha: new Date().toISOString().slice(0, 10),
    tipo: "enfermedad", horas: 8, justificada: true, motivo: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({
      employee_id: employees[0]?.id || "",
      fecha: new Date().toISOString().slice(0, 10),
      tipo: "enfermedad", horas: 8, justificada: true, motivo: "",
    });
  }, [open, employees]);

  const submit = async () => {
    if (!form.employee_id) return toast.error("Seleccioná un empleado");
    setSaving(true);
    try {
      await api.post("/hr/absences", { ...form, horas: parseFloat(form.horas) || 0 });
      toast.success("Ausencia registrada");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al registrar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="hr-new-abs-dialog">
        <DialogHeader>
          <DialogTitle>Registrar ausencia</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Empleado</label>
            <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
              <SelectTrigger className="rounded-sm" data-testid="hr-new-abs-employee">
                <SelectValue placeholder="Seleccionar…" />
              </SelectTrigger>
              <SelectContent>
                {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.nombre_completo}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Fecha</label>
              <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className="rounded-sm" data-testid="hr-new-abs-date" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Horas</label>
              <Input type="number" step="0.5" min="0" value={form.horas} onChange={(e) => setForm({ ...form, horas: e.target.value })} className="rounded-sm" data-testid="hr-new-abs-hours" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Tipo</label>
            <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
              <SelectTrigger className="rounded-sm" data-testid="hr-new-abs-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPO_AUS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Checkbox checked={form.justificada} onCheckedChange={(v) => setForm({ ...form, justificada: !!v })} data-testid="hr-new-abs-justified" />
            Justificada (no se descuenta de nómina)
          </label>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Motivo</label>
            <Textarea value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} rows={2} className="rounded-sm" data-testid="hr-new-abs-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="btn-primary rounded-sm" data-testid="hr-new-abs-submit">
            {saving ? "Guardando…" : "Registrar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────────────────────────────────────── Payroll ── */

function PayrollPanel({ employees, runs, onChanged }) {
  const [form, setForm] = useState({
    employee_id: "", periodo: new Date().toISOString().slice(0, 7),
    dias_trabajados: 30, horas_extra: 0, valor_hora_extra_override: 0,
    bonificaciones: 0, ccss_obrero_pct: 10.67, ccss_patronal_pct: 26.67,
    impuesto_renta: 0, otras_deducciones: 0, deducir_ausencias_no_justificadas: true,
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!form.employee_id && employees.length) {
      setForm(f => ({ ...f, employee_id: employees[0].id }));
    }
  }, [employees, form.employee_id]);

  const computePreview = async () => {
    if (!form.employee_id) return toast.error("Seleccioná un empleado");
    setBusy(true);
    try {
      const payload = {
        ...form,
        dias_trabajados: parseFloat(form.dias_trabajados) || 0,
        horas_extra: parseFloat(form.horas_extra) || 0,
        valor_hora_extra_override: parseFloat(form.valor_hora_extra_override) || 0,
        bonificaciones: parseFloat(form.bonificaciones) || 0,
        ccss_obrero_pct: parseFloat(form.ccss_obrero_pct) || 0,
        ccss_patronal_pct: parseFloat(form.ccss_patronal_pct) || 0,
        impuesto_renta: parseFloat(form.impuesto_renta) || 0,
        otras_deducciones: parseFloat(form.otras_deducciones) || 0,
      };
      const { data } = await api.post("/hr/payroll/preview", payload);
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error calculando nómina");
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    if (!preview) return toast.error("Calculá la vista previa antes de generar");
    setBusy(true);
    try {
      const payload = {
        ...form,
        dias_trabajados: parseFloat(form.dias_trabajados) || 0,
        horas_extra: parseFloat(form.horas_extra) || 0,
        valor_hora_extra_override: parseFloat(form.valor_hora_extra_override) || 0,
        bonificaciones: parseFloat(form.bonificaciones) || 0,
        ccss_obrero_pct: parseFloat(form.ccss_obrero_pct) || 0,
        ccss_patronal_pct: parseFloat(form.ccss_patronal_pct) || 0,
        impuesto_renta: parseFloat(form.impuesto_renta) || 0,
        otras_deducciones: parseFloat(form.otras_deducciones) || 0,
      };
      await api.post("/hr/payroll/generate", payload);
      toast.success("Boleta generada");
      setPreview(null);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error generando boleta");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async (runId, employeeName, periodo) => {
    try {
      const res = await api.get(`/hr/payroll/runs/${runId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `boleta_${periodo}_${employeeName.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error descargando PDF");
    }
  };

  const deleteRun = async (run) => {
    if (!window.confirm(`¿Eliminar boleta de ${run.employee_name} (${run.periodo})?`)) return;
    await api.delete(`/hr/payroll/runs/${run.id}`);
    toast.success("Boleta eliminada");
    onChanged();
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 rounded-sm border border-slate-200 bg-slate-50">
        <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
          <Calculator className="h-4 w-4 text-[#002FA7]" />
          Calculadora de nómina
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Empleado</label>
            <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
              <SelectTrigger className="rounded-sm bg-white" data-testid="hr-payroll-employee">
                <SelectValue placeholder="Seleccionar…" />
              </SelectTrigger>
              <SelectContent>
                {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.nombre_completo}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Periodo (YYYY-MM)</label>
            <Input type="month" value={form.periodo} onChange={(e) => setForm({ ...form, periodo: e.target.value })} className="rounded-sm bg-white" data-testid="hr-payroll-periodo" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Días trabajados</label>
            <Input type="number" min="0" max="31" value={form.dias_trabajados} onChange={(e) => setForm({ ...form, dias_trabajados: e.target.value })} className="rounded-sm bg-white" data-testid="hr-payroll-days" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Horas extra</label>
            <Input type="number" step="0.5" min="0" value={form.horas_extra} onChange={(e) => setForm({ ...form, horas_extra: e.target.value })} className="rounded-sm bg-white" data-testid="hr-payroll-overtime" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Bonificaciones (₡)</label>
            <Input type="number" min="0" value={form.bonificaciones} onChange={(e) => setForm({ ...form, bonificaciones: e.target.value })} className="rounded-sm bg-white" data-testid="hr-payroll-bonus" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Impuesto renta (₡)</label>
            <Input type="number" min="0" value={form.impuesto_renta} onChange={(e) => setForm({ ...form, impuesto_renta: e.target.value })} className="rounded-sm bg-white" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Otras deducciones (₡)</label>
            <Input type="number" min="0" value={form.otras_deducciones} onChange={(e) => setForm({ ...form, otras_deducciones: e.target.value })} className="rounded-sm bg-white" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">CCSS obrero %</label>
            <Input type="number" step="0.01" value={form.ccss_obrero_pct} onChange={(e) => setForm({ ...form, ccss_obrero_pct: e.target.value })} className="rounded-sm bg-white mono" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">CCSS patronal %</label>
            <Input type="number" step="0.01" value={form.ccss_patronal_pct} onChange={(e) => setForm({ ...form, ccss_patronal_pct: e.target.value })} className="rounded-sm bg-white mono" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 mt-3">
          <Checkbox
            checked={form.deducir_ausencias_no_justificadas}
            onCheckedChange={(v) => setForm({ ...form, deducir_ausencias_no_justificadas: !!v })}
            data-testid="hr-payroll-deduct-absences"
          />
          Descontar ausencias injustificadas y permisos sin goce de salario
        </label>

        <div className="flex justify-end gap-2 mt-4">
          <Button onClick={computePreview} disabled={busy} variant="outline" className="rounded-sm" data-testid="hr-payroll-preview-btn">
            {busy ? "Calculando…" : "Calcular vista previa"}
          </Button>
          <Button onClick={generate} disabled={busy || !preview} className="btn-primary rounded-sm" data-testid="hr-payroll-generate">
            <FileDown className="h-4 w-4 mr-1.5" />
            Generar boleta
          </Button>
        </div>

        {preview && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 bg-white border border-slate-200 rounded-sm p-3" data-testid="hr-payroll-preview-result">
            <Stat label="Salario bruto" value={formatMoney(preview.salario_bruto)} />
            <Stat label="Horas extra" value={formatMoney(preview.monto_horas_extra)} />
            <Stat label="Ausencias" value={formatMoney(-preview.ausencias_descuento)} negative={preview.ausencias_descuento > 0} />
            <Stat label="CCSS obrero" value={formatMoney(-preview.ccss_obrero_monto)} negative />
            <Stat label="Otras deduc." value={formatMoney(-(preview.impuesto_renta + preview.otras_deducciones))} negative={preview.impuesto_renta + preview.otras_deducciones > 0} />
            <Stat label="Total deduc." value={formatMoney(-preview.total_deducciones)} negative />
            <Stat label="CCSS patronal" value={formatMoney(preview.ccss_patronal_monto)} hint="aporte del empleador" />
            <Stat label="Neto a pagar" value={formatMoney(preview.neto_pagar)} highlight />
          </div>
        )}
      </Card>

      {/* Run history */}
      <div>
        <h3 className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-2">
          <UserCog className="h-4 w-4" />
          Boletas generadas ({runs.length})
        </h3>
        {runs.length === 0 ? (
          <EmptyState icon={AlertCircle} title="Sin boletas" description="Calculá y generá una boleta para verla acá." />
        ) : (
          <Card className="rounded-sm border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Periodo</th>
                  <th className="text-left px-3 py-2">Empleado</th>
                  <th className="text-right px-3 py-2">Bruto</th>
                  <th className="text-right px-3 py-2">Deducciones</th>
                  <th className="text-right px-3 py-2">Neto</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {runs.map(r => (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`hr-payroll-run-${r.id}`}>
                    <td className="px-3 py-2 mono">{r.periodo}</td>
                    <td className="px-3 py-2 truncate max-w-[200px]">{r.employee_name}</td>
                    <td className="px-3 py-2 text-right mono">{formatMoney(r.salario_bruto)}</td>
                    <td className="px-3 py-2 text-right mono text-red-600">-{formatMoney(r.total_deducciones)}</td>
                    <td className="px-3 py-2 text-right mono font-black text-emerald-700">{formatMoney(r.neto_pagar)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(r.id, r.employee_name, r.periodo)} className="rounded-sm h-7 mr-1" data-testid={`hr-payroll-pdf-${r.id}`} title="Descargar boleta PDF">
                        <FileDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteRun(r)} className="rounded-sm h-7 text-slate-400 hover:text-red-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight = false, negative = false, hint = "" }) {
  return (
    <div className={`p-2 rounded-sm ${highlight ? "bg-emerald-50 border border-emerald-300" : "bg-slate-50"}`}>
      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</p>
      <p
        className={`mono font-black ${highlight ? "text-emerald-700 text-base" : negative ? "text-red-600" : "text-slate-900"}`}
        style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
      >
        {value}
      </p>
      {hint && <p className="text-[9px] italic text-slate-400">{hint}</p>}
    </div>
  );
}
